/**
 * ADR-0168 — the consequence copy shown before a tracking reset.
 *
 * These sentences are duplicated deliberately from the hub's composer
 * (`watchConditions/resetTracking.ts`): the door states its consequence
 * server-side, the console states the same one client-side from config it
 * already holds, and neither surface should have to make a round trip just to
 * ask what its own confirm dialog says. The duplication is held honest by test
 * — `watchTracking.test.ts` pins the exact strings, so a change on one side
 * without the other shows up as a failure rather than as two surfaces quietly
 * telling a person different things about the same button.
 *
 * Getting this wrong is not cosmetic: reset on a sustain-0 watch re-alerts every
 * still-matching row on the next evaluation, and on a change-detection watch it
 * does the opposite — erases the baselines and guarantees silence for one cycle.
 */

export type ResetScope = "watch" | "identity";

export interface ResetCopyArgs {
  trigger: { type?: string } | null | undefined;
  sustainedForMinutes: number | undefined;
  responses: Array<{ type?: string }> | undefined;
  scope: ResetScope;
  counts: { tracked: number; matching: number };
  identifyingValues?: Record<string, unknown> | null;
}

export function describeIdentity(values: Record<string, unknown> | null | undefined): string {
  if (!values) return "this row";
  const parts = Object.entries(values).map(([col, val]) => `${col} ${val ?? "—"}`);
  return parts.length > 0 ? parts.join(", ") : "this row";
}

export function composeResetScopeLine(args: ResetCopyArgs): string {
  if (args.scope === "identity") {
    return `Clears tracking for ${describeIdentity(args.identifyingValues)}.`;
  }
  const { tracked, matching } = args.counts;
  return `Clears tracking for all ${tracked} ${tracked === 1 ? "row" : "rows"} (${matching} currently matching).`;
}

export function composeResetEffectLine(args: ResetCopyArgs): string {
  const type = args.trigger?.type ?? "row_match";
  if (type === "row_delta_pct_gt" || type === "row_delta_abs_gt") {
    return (
      "Change-detection baselines are erased. The next evaluation records fresh " +
      "baselines and does not alert; change detection resumes on the evaluation after that."
    );
  }
  const sustained = args.sustainedForMinutes ?? 0;
  if (sustained > 0) {
    return (
      `Rows that still match are treated as newly matching — the ${sustained}-minute ` +
      "sustain window restarts before anything alerts."
    );
  }
  return (
    "Every row that still matches will alert again on the next evaluation — action items " +
    "re-notify or are re-created, and emails/webhooks send again."
  );
}

export function composeResetActionItemLine(args: ResetCopyArgs): string | null {
  const hasActionItem = (args.responses ?? []).some((r) => r?.type === "action_item");
  if (!hasActionItem) return null;
  return "Rows whose action items were dismissed or resolved will get new items.";
}

export function composeResetConfirmCopy(args: ResetCopyArgs): string {
  return [composeResetScopeLine(args), composeResetEffectLine(args), composeResetActionItemLine(args)]
    .filter((line): line is string => line != null)
    .join(" ");
}
